<script setup lang="ts">
import { watch, onUnmounted } from 'vue';

const props = withDefaults(defineProps<{
  numbersArray: Array<number>,
  showCommas?: boolean,
}>(), {
  showCommas: false,
});

const previousNumbers = ref<Array<number>>([...props.numbersArray]);
const flipping = ref<Array<boolean>>(props.numbersArray.map(() => false));
const timeoutIds = ref<Array<ReturnType<typeof setTimeout> | null>>(
  props.numbersArray.map(() => null),
);

const DIGITS_PER_GROUP = 3;

const shouldShowComma = (index: number) => {
  const len = props.numbersArray.length;
  return props.showCommas && index > 0 && (len - index) % DIGITS_PER_GROUP === 0;
};

watch(() => props.numbersArray, (newVal) => {
  // Handle array length changes
  if (newVal.length !== previousNumbers.value.length) {
    // Clear all existing timeouts
    timeoutIds.value.forEach(id => id && clearTimeout(id));

    previousNumbers.value = [...newVal];
    flipping.value = newVal.map(() => false);
    timeoutIds.value = newVal.map(() => null);
    return;
  }

  // Trigger flip animation for changed digits
  newVal.forEach((num, index) => {
    if (num !== previousNumbers.value[index]) {
      // Clear any existing timeout for this digit
      if (timeoutIds.value[index]) {
        clearTimeout(timeoutIds.value[index]!);
      }

      flipping.value[index] = true;
      timeoutIds.value[index] = setTimeout(() => {
        previousNumbers.value[index] = num;
        flipping.value[index] = false;
        timeoutIds.value[index] = null;
      }, 300);
    }
  });
}, { deep: true });

// Cleanup on unmount
onUnmounted(() => {
  timeoutIds.value.forEach(id => id && clearTimeout(id));
});
</script>

<template>
  <div class="flip-counter-container">
    <div class="flip-counter">
      <template v-for="(number, index) in numbersArray" :key="index">
        <div v-if="shouldShowComma(index)" class="flip-counter__comma">
          ,
        </div>
        <div class="flip-counter__unit">
          <div class="flip-counter__card flip-counter__card--bottom">
            <span class="flip-counter__number">{{ number }}</span>
          </div>
          <div class="flip-counter__card flip-counter__card--top">
            <span class="flip-counter__number">{{ number }}</span>
          </div>
          <div
            :class="[
              'flip-counter__card',
              'flip-counter__card--top',
              'flip-counter__card--flip-top',
              { 'flipping': flipping[index] }
            ]"
          >
            <span class="flip-counter__number">{{ previousNumbers[index] }}</span>
          </div>
          <div
            :class="[
              'flip-counter__card',
              'flip-counter__card--bottom',
              'flip-counter__card--flip-bottom',
              { 'flipping': flipping[index] }
            ]"
          >
            <span class="flip-counter__number">{{ number }}</span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
  @import '~/assets/css/mixins/_mixins.index.pcss';

.flip-counter-container {
  container-type: inline-size;
  width: 100%;
}
.flip-counter {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
}

.flip-counter__comma {
  @mixin h2;
  color: theme('colors.green.30');
  align-self: flex-end;
  padding-bottom: 0.25rem;
}

.flip-counter__unit {
  position: relative;
  width: 3.75rem;
  height: 3.5rem;
  perspective: 300px;
  border-radius: 0.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.2);
}

.flip-counter__unit::before,
.flip-counter__unit::after {
  content: '';
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 0.25rem;
  height: 0.75rem;
  background-color: white;
  border-radius: 0.125rem;
  z-index: 10;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.flip-counter__unit::before {
  left: 0.1875rem;
}

.flip-counter__unit::after {
  right: 0.1875rem;
}

.flip-counter__card {
  position: absolute;
  left: 0;
  width: 100%;
  height: calc(50% - 0.125rem);
  overflow: hidden;
  background-color: theme('colors.beige.DEFAULT');
  backface-visibility: hidden;
}

.flip-counter__card--top {
  top: 0;
  border-radius: theme('spacing.8') theme('spacing.8') theme('spacing.4') theme('spacing.4');
}

.flip-counter__card--bottom {
  bottom: 0;
  border-radius: theme('spacing.4') theme('spacing.4') theme('spacing.8') theme('spacing.8');
}

.flip-counter__card--flip-top {
  transform-origin: bottom center;
  z-index: 2;
}

.flip-counter__card--flip-top.flipping {
  animation: flip-top 0.3s ease-in forwards;
}

.flip-counter__card--flip-bottom {
  transform-origin: top center;
  z-index: 1;
}

.flip-counter__card--flip-bottom.flipping {
  animation: flip-bottom 0.3s 0.15s ease-out forwards;
}

.flip-counter__number {
  position: absolute;
  left: 0;
  width: 100%;
  height: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: theme('colors.green.30');
  @mixin h2;
  font-size: 2.5rem;

  @screen tablet-wide {
    height: 5rem;
    font-size: 4.6rem;
  }
}

.flip-counter__card--top .flip-counter__number {
  top: 0;
}

.flip-counter__card--bottom .flip-counter__number {
  top: auto;
  bottom: 0;
}

.flip-counter__card--bottom::before {
  display: none;
}

.flip-counter__card--bottom::after {
  display: none;
}

@keyframes flip-top {
  0% {
    transform: rotateX(0deg);
  }
  100% {
    transform: rotateX(-90deg);
  }
}

@keyframes flip-bottom {
  0% {
    transform: rotateX(90deg);
  }
  100% {
    transform: rotateX(0deg);
  }
}

@media (min-width: 768px) {
  .flip-counter {
    gap: 0.75rem;
  }

  .flip-counter__unit {
    width: 5rem;
    height: 5.875rem;
  }

  .flip-counter__unit::before,
  .flip-counter__unit::after {
    width: 0.3125rem;
    height: 1rem;
  }

  .flip-counter__number {
    height: 5.875rem;
  }

  .flip-counter__comma {
    font-size: 4rem;
  }
}
</style>
